/**
 * Easter patterns — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const easterPatternsPack: ToilePack = {
    slug: 'easter-patterns',
    category: 'season',
    name: 'Easter patterns',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'eggs', label: 'Egg bands', subject: 'Painted eggs in neat bands with dots between rows'},
        {key: 'blossom', label: 'Blossom and bees', subject: 'Blossom sprigs and small bees scattered evenly across the frame'},
        {key: 'chicks', label: 'Chicks and daisies', subject: 'Small chicks and daisies tossed across the frame with even spacing'},
    ],
    styles: [
        {key: 'pastel', label: 'Pastel repeat', style: 'soft flat surface pattern, rounded motifs, even spacing, seamless repeat', palette: 'Pastel pink, mint, lilac and yellow on cream', framing: 'The motif repeated across the whole frame with even spacing, tileable edges'},
        {key: 'vintage', label: 'Vintage repeat', style: 'vintage easter paper, engraved motifs, slight grain, seamless repeat', palette: 'Rose, sage and cream', framing: 'The motif repeated across the whole frame, tileable edges'},
        {key: 'modern', label: 'Modern repeat', style: 'modern minimal surface design, flat simplified shapes, generous spacing, seamless repeat', palette: 'Two colours: sage green on white with a yellow accent', framing: 'Sparse repeat across the whole frame, tileable edges, no borders'},
    ],
    directions: ['a bunny motif instead', 'in pastel blue and gold', 'a larger repeat scale'],
    directionHint: 'try: a bunny motif instead',
    seo: {
        title: 'Easter patterns: eggs, blossom, chicks',
        metaDescription: 'Nine Easter seamless patterns — egg bands, blossom and bees, chicks and daisies — for fabric or paper, in pastel, vintage or modern repeat.',
        h1: 'Easter seamless patterns: egg bands, blossom and chicks',
        tagline: 'Nine patterns, three spring motifs, three treatments each — tileable for fabric, paper or screens.',
        intro: 'A spring surface pattern scattered evenly: painted eggs in neat bands, blossom sprigs and small bees, chicks tossed among daisies. Each renders as a soft pastel repeat, a vintage engraved print, or a sparse modern minimal repeat, tileable with no visible seam. Ask for a different motif, like a bunny, and the same tileable structure carries it.',
        useCases: [
            'a fabric or quilting design',
            'a printable gift wrap or paper source',
            'a phone or desktop background pattern',
            'a small-batch product packaging design',
        ],
        faq: [
            {q: 'Does the pattern tile without a visible seam?', a: 'Yes — every design is a seamless repeat with tileable edges, ready for fabric, paper or a screen background.'},
            {q: 'Can I get a different motif, like a bunny?', a: 'Yes — the direction field takes any spring motif, and the same tileable structure renders it in all three treatments.'},
            {q: "What's the difference between the three treatments?", a: 'Pastel is a soft rounded repeat, vintage has an engraved-print texture, and modern minimal is a sparse two-colour repeat.'},
            {q: 'What size does this print or export at?', a: 'The pattern is built to repeat, not a fixed sheet — scale it to whatever the fabric, paper or screen needs.'},
        ],
    },
}
